import React, {useEffect, useState} from 'react';
import {connect} from 'react-redux';
import {Link, RouteComponentProps} from 'react-router-dom';
import {Button, ButtonGroup, Col, Label, Row} from 'reactstrap';
import {AvField, AvForm, AvGroup, AvInput} from 'availity-reactstrap-validation';
import {FontAwesomeIcon} from '@fortawesome/react-fontawesome';
import {IRootState} from 'app/shared/reducers';
import {createEntity, getEntity, reset, updateEntity} from './symptom.reducer';
import {RiskProfile} from "app/shared/model/enumerations/risk-profile.model";
import {getAllByType as getCountries} from "app/entities/region/region.reducer";
import { useTranslation } from 'app/shared/util/translation-utils';

export interface ISymptomUpdateProps extends StateProps, DispatchProps, RouteComponentProps<{ id: string }> {
}

export const SymptomUpdate = (props: ISymptomUpdateProps) => {
  const { t } = useTranslation();
  const [isNew, setIsNew] = useState(!props.match.params || !props.match.params.id);
  const [countryId, setCountryId] = useState(null)
  const [activeStatus, setActiveStatus] = useState(true);
  const [staticStatus, setStaticStatus] = useState(true);
  const [riskProfile, setRiskProfile] = useState(null);
  const {symptomEntity, loading, updating} = props;

  const handleClose = () => {
    props.history.push('/symptom');
  };

  useEffect(() => {
    if (isNew) {
      const params = new URLSearchParams(props.location.search);
      setCountryId(params.get('country_id'))
      props.reset();
    } else {
      props.getEntity(props.match.params.id);
    }
    props.getCountries();
    return () => {
      props.reset();
    }
  }, []);

  useEffect(() => {
    if (props.updateSuccess) {
      handleClose();
    }
  }, [props.updateSuccess]);

  useEffect(() => {
    if(symptomEntity.id) {
      setActiveStatus(symptomEntity?.isActive);
      setStaticStatus(symptomEntity?.isStatic);
      setRiskProfile(symptomEntity?.riskProfile);
    }
  }, [symptomEntity]);

  const saveEntity = (event, errors, values) => {
    if (errors.length === 0) {
      const cid = isNew ? countryId : symptomEntity.country.id;
      const country = props.countries.find(c => c.id === cid)
      const overrideStaticStatus = (riskProfile === RiskProfile.HIGH) ? staticStatus : false;

      const entity = {
        ...symptomEntity,
        ...values,
        riskProfile,
        isActive: activeStatus,
        isStatic: overrideStaticStatus,
        country
      };

      if (isNew) {
        props.createEntity(entity);
      } else {
        props.updateEntity(entity);
      }
    }
  };

  return (
    <div>
      <Row className="justify-content-center">
        <Col md="8">
          <h2 id="watotocareApp.symptom.home.createOrEditLabel">{t('symptom.home.createOrEditLabel')}</h2>
        </Col>
      </Row>
      <Row className="justify-content-center">
        <Col md="8">
          {loading ? (
            <p>Loading...</p>
          ) : (
            <AvForm model={isNew ? {} : symptomEntity} onSubmit={saveEntity}>
              {!isNew ? (
                <AvGroup>
                  <Label for="symptom-id">{t('common.fields.id')}</Label>
                  <AvInput id="symptom-id" type="text" className="form-control" name="id" required readOnly/>
                </AvGroup>
              ) : null}

              <br/>
              <Row class="justify-content-center">
                <Col md="6">
                  <AvGroup>
                    <Label id="isActive" for="symptom-isActive">
                      {t('symptom.update.activeStatus')}
                    </Label>
                    <ButtonGroup style={{marginLeft: "30px"}}>
                      <Button
                        color={activeStatus ? 'primary' : 'secondary'}
                        onClick={(e) => {
                          setActiveStatus(true)
                        }}
                      >
                        {t('common.status.active')}
                      </Button>
                      <Button
                        color={!activeStatus ? 'primary' : 'secondary'}
                        onClick={() => {
                          setActiveStatus(false)
                        }}
                      >
                        {t('common.status.inactive')}
                      </Button>
                    </ButtonGroup>
                  </AvGroup>
                </Col>

                <Col md="6">
                  <AvGroup>
                    <Label id="isStatic" for="condition-isStatic">
                      {t('symptom.fields.isStatic')}
                    </Label>
                    <ButtonGroup style={{marginLeft: "30px"}}>
                      <Button
                        color={(staticStatus && riskProfile === RiskProfile.HIGH) ? 'primary' : 'secondary'}
                        disabled={riskProfile !== RiskProfile.HIGH}
                        onClick={(e) => {
                          setStaticStatus(true)
                        }}
                      >
                        {t('common.yes')}
                      </Button>
                      <Button
                        color={(!staticStatus && riskProfile === RiskProfile.HIGH) ? 'primary' : 'secondary'}
                        disabled={riskProfile !== RiskProfile.HIGH}
                        onClick={() => {
                          setStaticStatus(false)
                        }}
                      >
                        {t('common.no')}
                      </Button>
                    </ButtonGroup>
                  </AvGroup>
                </Col>
              </Row>

              <AvGroup>
                <Label id="emergencyTypeLabel" for="symptom-emergencyType">
                  {t('symptom.fields.emergencyType')}
                </Label>
                <AvInput
                  id="symptom-emergencyType"
                  type="select"
                  className="form-control"
                  name="emergencyType"
                  value={(!isNew && symptomEntity.emergencyType) || 'MATERNAL'}
                >
                  <option value="MATERNAL">MATERNAL</option>
                  <option value="NEONATAL">NEONATAL</option>
                </AvInput>
              </AvGroup>
              <AvGroup>
                <Label id="enConditionLabel" for="symptom-enCondition">
                  {t('symptom.fields.enCondition')}
                </Label>
                <AvField
                  id="symptom-enCondition"
                  type="text"
                  name="enCondition"
                  validate={{
                    required: {value: true, errorMessage: t('common.validation.required')},
                  }}
                />
              </AvGroup>
              <AvGroup>
                <Label id="stConditionLabel" for="symptom-stCondition">
                  {t('symptom.fields.stCondition')}
                </Label>
                <AvField
                  id="symptom-stCondition"
                  type="text"
                  name="stCondition"
                  validate={{
                    required: {value: true, errorMessage: t('common.validation.required')},
                  }}
                />
              </AvGroup>
              <AvGroup>
                <Label id="requiredFacilityLevelLabel" for="symptom-requiredFacilityLevel">
                  {t('symptom.fields.requiredFacilityLevel')}
                </Label>
                <AvField
                  id="symptom-requiredFacilityLevel"
                  type="number"
                  className="form-control"
                  name="requiredFacilityLevel"
                  validate={{
                    required: {value: true, errorMessage: t('common.validation.required')},
                    number: {value: true, errorMessage: t('common.validation.mustBeNumber')},
                  }}
                  min={0}
                  step={1}
                />
              </AvGroup>
              <AvGroup>
                <Label id="requiredVehicleLevelLabel" for="symptom-requiredVehicleLevel">
                  {t('symptom.fields.requiredVehicleLevel')}
                </Label>
                <AvField
                  id="symptom-requiredVehicleLevel"
                  type="number"
                  className="form-control"
                  name="requiredVehicleLevel"
                  validate={{
                    required: {value: true, errorMessage: t('common.validation.required')},
                    number: {value: true, errorMessage: t('common.validation.mustBeNumber')},
                  }}
                  min={0}
                  step={1}
                />
              </AvGroup>
              <AvGroup>
                <Label id="riskProfilLabel" for="symptom-riskProfile">
                  {t('symptom.fields.riskProfile')}
                </Label>
                <AvInput
                  id="symptom-riskProfile"
                  type="select"
                  className="form-control"
                  name="riskProfile"
                  value={symptomEntity.riskProfile}
                  onChange={(event) => {
                    setRiskProfile(event.target.value)
                  }}
                  validate={{
                    required: {value: true, errorMessage: t('common.validation.required')},
                  }}
                >
                  {isNew && <option disabled selected value=''>{t('symptom.update.selectOption')}</option>}
                  {Object.values(RiskProfile).map(val => (
                    <option key={val} value={val}>{val}</option>
                  ))}
                </AvInput>
              </AvGroup>
              <ButtonGroup className="d-flex justify-content-between">
                <Button color="secondary" onClick={handleClose}>
                  <FontAwesomeIcon icon="ban" />
                  &nbsp;
                  <span className="d-none d-md-inline">{t('common.actions.back')}</span>
                </Button>
                <Button color="primary" id="save-entity" type="submit" disabled={updating}>
                  <FontAwesomeIcon icon="save" />
                  &nbsp;
                  <span className="d-none d-md-inline">{t('common.actions.save')}</span>
                </Button>
              </ButtonGroup>
            </AvForm>
          )}
        </Col>
      </Row>
    </div>
  );
};

const mapStateToProps = (storeState: IRootState) => ({
  symptomEntity: storeState.symptom.entity,
  loading: storeState.symptom.loading,
  updating: storeState.symptom.updating,
  updateSuccess: storeState.symptom.updateSuccess,
  countries: storeState.region.entities,
});

const mapDispatchToProps = {
  getCountries,
  getEntity,
  updateEntity,
  createEntity,
  reset,
};

type StateProps = ReturnType<typeof mapStateToProps>;
type DispatchProps = typeof mapDispatchToProps;

export default connect(mapStateToProps, mapDispatchToProps)(SymptomUpdate);
